// ============================================================================
// CalendarSubscribeCard — the shared "Sync to your calendar" / "Add my training
// to my calendar" subscription section, reused by BOTH realms:
//   • TRAINER  — in the Calendar area + Account (their feed = all clients)
//   • CLIENT   — in the portal (their own training only)
// ----------------------------------------------------------------------------
// Presentational only: the caller wires the data + rotate mutation (so each
// realm hits its own endpoint and React Query cache) and passes a `t` already
// scoped to a `calendarSync.*` key subtree whose KEYS ARE IDENTICAL across the
// trainer and client namespaces. That keeps one component, two endpoints, and
// no copy drift.
//
// What it makes unmistakable: this is a READ-ONLY subscription. Trainings appear
// in the user's own calendar and AUTO-UPDATE; edits still happen in ViccoFitness.
// The token-gated feed URL is the secret (QR-check-in posture), so a "Rotate
// link" action revokes a leaked URL and mints a fresh one. Mobile-first, Pulse,
// reduced-motion aware; never restyles a locked Pulse primitive.
// ============================================================================
import { useState } from "react";
import type { TFunction } from "i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Badge } from "./Badge";
import { Banner } from "./Banner";
import { Button } from "./Button";
import { Card, CardHeader } from "./Card";

const EASE = [0.22, 0.61, 0.36, 1] as const;

export interface CalendarSubscribeCardProps {
  /** A `t` scoped so `t("title")`, `t("howTo.google")`, etc. resolve under the
   *  caller's `calendarSync` subtree (identical key shape in both namespaces). */
  t: TFunction;
  /** The token-gated, read-only feed URL (absolute). */
  url: string | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Rotate the token: revoke the old URL, mint a new one. */
  onRotate: () => void;
  isRotating: boolean;
  rotateFailed: boolean;
  className?: string;
}

export function CalendarSubscribeCard({
  t,
  url,
  isLoading,
  isError,
  onRetry,
  onRotate,
  isRotating,
  rotateFailed,
  className,
}: CalendarSubscribeCardProps) {
  const reduce = useReducedMotion();
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const rotate = () => {
    setConfirmRotate(false);
    setCopied(false);
    onRotate();
  };

  return (
    <Card className={className}>
      <CardHeader
        title={t("title")}
        subtitle={t("subtitle")}
        action={
          <Badge tone="info" dot>
            {t("readOnlyBadge")}
          </Badge>
        }
      />

      <div className="mt-4 space-y-4">
        {/* The read-only promise, stated plainly. */}
        <Banner tone="info" title={t("readOnly.title")}>
          {t("readOnly.body")}
        </Banner>

        {isError ? (
          <Banner
            tone="danger"
            title={t("error.title")}
            action={
              <Button size="sm" variant="ghost" onClick={onRetry}>
                {t("error.retry")}
              </Button>
            }
          >
            {t("error.body")}
          </Banner>
        ) : isLoading || !url ? (
          <div
            className="h-[4.5rem] animate-pulse rounded-md bg-surface-sunken"
            aria-hidden
          />
        ) : (
          <>
            {/* The feed URL + copy button. */}
            <div className="rounded-md bg-violet-soft p-4">
              <p className="font-display text-sm font-bold text-ember-600">
                {t("urlLabel")}
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code
                  className="min-w-0 flex-1 truncate rounded-sm bg-surface px-3 py-2 text-xs text-body-muted ring-1 ring-line"
                  title={url}
                >
                  {url}
                </code>
                <Button
                  size="sm"
                  variant="secondary"
                  className="shrink-0"
                  onClick={() => void copy()}
                >
                  {copied ? t("copied") : t("copy")}
                </Button>
              </div>
            </div>

            {/* How to subscribe — short, app-agnostic steps. */}
            <div className="rounded-md bg-surface-sunken px-4 py-3">
              <p className="font-display text-sm font-bold text-body">
                {t("howTo.title")}
              </p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-body-soft">
                <li>{t("howTo.google")}</li>
                <li>{t("howTo.apple")}</li>
                <li>{t("howTo.outlook")}</li>
              </ol>
              <p className="mt-2 text-xs text-body-faint">{t("howTo.note")}</p>
            </div>

            {/* Rotate link — revokes a leaked URL. */}
            <div className="flex flex-col gap-2 border-t border-line pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-display text-sm font-bold text-body">
                  {t("rotate.title")}
                </p>
                <p className="mt-0.5 text-sm text-body-soft">
                  {t("rotate.body")}
                </p>
              </div>

              <AnimatePresence initial={false} mode="wait">
                {confirmRotate ? (
                  <motion.div
                    key="confirm"
                    initial={reduce ? false : { opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, x: 8 }}
                    transition={{ duration: 0.2, ease: EASE }}
                    className="flex shrink-0 items-center gap-2"
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmRotate(false)}
                      disabled={isRotating}
                    >
                      {t("rotate.cancel")}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={rotate}
                      disabled={isRotating}
                    >
                      {isRotating ? t("rotate.rotating") : t("rotate.confirm")}
                    </Button>
                  </motion.div>
                ) : (
                  <Button
                    key="trigger"
                    size="sm"
                    variant="ghost"
                    className="shrink-0"
                    onClick={() => setConfirmRotate(true)}
                  >
                    {t("rotate.action")}
                  </Button>
                )}
              </AnimatePresence>
            </div>

            {rotateFailed && (
              <Banner tone="danger" title={t("rotate.failed")} />
            )}
          </>
        )}
      </div>
    </Card>
  );
}
